/**
 * Njoftimi kalimtar i aplikacionit — i vetmi, i përdorur nga ~30 ekrane.
 *
 * Pamja e mëparshme ishte `variant="filled"` mbi tonet `main` të paletës. Ato
 * tone janë të ndritura, ndaj MUI-ja llogariste tekst të errët mbi to, dhe
 * rregulli `border: '1px solid'` te tema — pa ngjyrë të caktuar — e merrte
 * kufirin nga `currentColor`, pra po ai tekst i errët. Dilte një kuti e gjelbër
 * me kontur thuajse të zi: ngjitëse, jo njoftim. Rrënja u mbyll te tema; këtu
 * ndërtohet pamja që duam.
 *
 * Sipërfaqe e bardhë, ikonë me ngjyrë dhe një shirit 3px majtas për ashpërsinë —
 * i njëjti motiv si te shiriti i Rregullimeve të pagës. Teksti mbetet gjithnjë
 * i errët mbi të bardhë, pra lexohet njësoj mirë në të katër gjendjet.
 *
 * Poshtë në qendër. E paracaktuara e MUI-t, poshtë-majtas, binte mbi menynë
 * anësore dhe mbulonte zërat e fundit; qendra nuk i takon asnjë skaji dhe e ka
 * të njëjtën largësi nga çdo pikë ku sapo klikove. Në celular `index.css` e
 * shtrin gjithsesi në tërë gjerësinë, mbi shiritin e poshtëm.
 */
import React, { useState, forwardRef, useEffect, useCallback } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Slide from '@mui/material/Slide';
import MuiAlert from '@mui/material/Alert';
import { prefersReducedMotion } from '../../app/theme';

const SEVERITIES = ['success', 'error', 'warning', 'info'];

// Gabimi lexohet, nuk kapet me bisht të syrit: i jepet dyfishi i kohës.
const DURATION = { error: 6000, warning: 5000, success: 3500, info: 3500 };

const SlideUp = forwardRef(function SlideUp(props, ref) {
  return <Slide {...props} direction="up" ref={ref} />;
});

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert ref={ref} variant="standard" {...props} />;
});

export default function Toasts(props) {
  const { open, message, variant, severity: sevProp, onClose,
    autoHideDuration, anchorOrigin } = props;

  // Vetëm mbyllja mbahet lokalisht, dhe vetëm kur prindi nuk e trajton vetë.
  // Më parë i gjithë objekti i props-eve pasqyrohej në gjendje; ajo pasqyrë e
  // humbte mbylljen, sepse prindi mbetej `open: true` dhe rifreskimi i tij i
  // radhës e rikthente njoftimin që përdoruesi sapo e kishte mbyllur.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => { if (open) setDismissed(false); }, [open, message]);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    if (onClose) onClose(event, reason);
    else setDismissed(true);
  }, [onClose]);

  // `variant` është fjala e prindit për ashpërsinë; disa ekrane e quajnë
  // `severity`. Një vlerë bosh ose e panjohur do ta linte Alert-in pa ngjyrë.
  const sev = SEVERITIES.includes(variant) ? variant
    : SEVERITIES.includes(sevProp) ? sevProp
      : 'info';

  // Ndihmësi i temës, jo një pyetje e dytë për të njëjtën gjë.
  const still = prefersReducedMotion();

  return (
    <Snackbar
      open={Boolean(open) && !dismissed}
      autoHideDuration={autoHideDuration ?? DURATION[sev]}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      transitionDuration={still ? 0 : undefined}
      anchorOrigin={anchorOrigin || { vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={sev}
        onClose={handleClose}
        sx={(theme) => {
          const tone = theme.palette[sev];
          return {
            minWidth: { xs: 'auto', sm: 320 },
            maxWidth: { xs: '100%', sm: 460 },
            alignItems: 'center',
            bgcolor: 'background.paper',
            color: 'text.primary',
            fontWeight: 500,
            borderColor: 'divider',
            // Shiriti është hije, jo kufi: nuk zhvendos asnjë piksel të tekstit.
            boxShadow: `inset 3px 0 0 0 ${tone.main}, ${theme.shadows[5]}`,
            pl: 2.25,
            // Këtu përmbajtja është gjithnjë një fjali e vetme, ndaj rreshtimi
            // në qendër është i sigurt — ndryshe nga tema, që duhet t'i shërbejë
            // edhe alerteve me titull.
            '& .MuiAlert-message': { display: 'flex', alignItems: 'center', minHeight: 22 },
            '& .MuiAlert-icon':   { color: tone.onSoft, fontSize: 20, alignItems: 'center' },
            '& .MuiAlert-action': { color: theme.palette.grey[400] },
            '& .MuiAlert-action .MuiIconButton-root:hover': {
              color: theme.palette.grey[700],
              bgcolor: theme.palette.grey[100],
            },
          };
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
}
